'use client';

import { liveUpdatesIp, type LiveUpdateTopic } from '@/data/api';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type DependencyList, type ReactNode } from 'react';

type LiveDataMessage = { op: 'create' | 'update' | 'delete'; key: string; data: any };
type Subscription = { key: string; topic: LiveUpdateTopic; options?: Record<string, unknown>; listener: (message: LiveDataMessage) => void };
type Status = 'disabled' | 'connecting' | 'authenticated' | 'closed';
type LiveUpdateContextValue = { subscribe: (topic: LiveUpdateTopic, options: Record<string, unknown> | undefined, listener: (message: LiveDataMessage) => void) => string; unsubscribe: (key: string) => void; status: Status; generation: number };

const Context = createContext<LiveUpdateContextValue>({} as LiveUpdateContextValue);

function key() {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function dependencyKey(value: unknown) {
    try { return JSON.stringify(value); }
    catch { return String(value); }
}

export function LiveUpdateContext({ children, enabled = true }: { children?: ReactNode; enabled?: boolean }) {
    const socket = useRef<WebSocket | null>(null);
    const subscriptions = useRef(new Map<string, Subscription>());
    const reconnect = useRef<ReturnType<typeof setTimeout> | null>(null);
    const ping = useRef<ReturnType<typeof setInterval> | null>(null);
    const manual = useRef(false);
    const [status, setStatus] = useState<Status>(enabled ? 'connecting' : 'disabled');
    const [generation, setGeneration] = useState(0);

    const send = useCallback((message: object) => {
        if (socket.current?.readyState !== WebSocket.OPEN) return false;
        socket.current.send(JSON.stringify(message));
        return true;
    }, []);

    const connect = useCallback(() => {
        if (!enabled || typeof window === 'undefined') return;
        if (socket.current && [WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.current.readyState as any)) return;

        manual.current = false;
        setStatus('connecting');
        const ws = new WebSocket(liveUpdatesIp);
        socket.current = ws;

        ws.addEventListener('message', event => {
            let message: any;
            try { message = JSON.parse(event.data); }
            catch { return; }

            if (message.op === 'authenticated') {
                setStatus('authenticated');
                setGeneration(value => value + 1);
                for (const sub of subscriptions.current.values()) send({ op: 'sub', key: sub.key, topic: sub.topic, options: sub.options });
                return;
            }
            if (message.op === 'error') {
                console.error('[live-update]', message.message);
                return;
            }
            if (message.op === 'create' || message.op === 'update' || message.op === 'delete') subscriptions.current.get(message.key)?.listener(message);
        });

        ws.addEventListener('close', () => {
            socket.current = null;
            setStatus('closed');
            if (ping.current) clearInterval(ping.current);
            ping.current = null;
            if (!manual.current) reconnect.current = setTimeout(connect, 1000);
        });
        ws.addEventListener('error', () => ws.close());

        ping.current = setInterval(() => send({ op: 'ping' }), 30_000);
    }, [enabled, send]);

    useEffect(() => {
        if (!enabled) {
            setStatus('disabled');
            return;
        }
        connect();
        return () => {
            manual.current = true;
            if (reconnect.current) clearTimeout(reconnect.current);
            if (ping.current) clearInterval(ping.current);
            reconnect.current = null;
            ping.current = null;
            socket.current?.close();
            socket.current = null;
        };
    }, [connect, enabled]);

    const subscribe = useCallback<LiveUpdateContextValue['subscribe']>((topic, options, listener) => {
        const id = key();
        subscriptions.current.set(id, { key: id, topic, options, listener });
        send({ op: 'sub', key: id, topic, options });
        return id;
    }, [send]);

    const unsubscribe = useCallback((id: string) => {
        const sub = subscriptions.current.get(id);
        if (!sub) return;
        send({ op: 'unsub', key: id, topic: sub.topic, options: sub.options });
        subscriptions.current.delete(id);
    }, [send]);

    const value = useMemo(() => ({ subscribe, unsubscribe, status, generation }), [generation, status, subscribe, unsubscribe]);
    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useLiveUpdate(topic: LiveUpdateTopic, options: Record<string, unknown> | undefined, listener: (message: LiveDataMessage) => void, dependencies: DependencyList = []) {
    const { subscribe, unsubscribe } = useContext(Context);
    const listenerRef = useRef(listener);
    listenerRef.current = listener;
    const optionsKey = dependencyKey(options);
    const dependenciesKey = dependencyKey(dependencies);

    useEffect(() => {
        const id = subscribe(topic, options, message => listenerRef.current(message));
        return () => unsubscribe(id);
    }, [topic, optionsKey, dependenciesKey, subscribe, unsubscribe]);
}


export function useLiveUpdateMany(topic: LiveUpdateTopic, options: Record<string, unknown>[], listener: (message: LiveDataMessage) => void, dependencies: DependencyList = []) {
    const { subscribe, unsubscribe } = useContext(Context);
    const listenerRef = useRef(listener);
    listenerRef.current = listener;
    const optionsKey = dependencyKey(options);
    const dependenciesKey = dependencyKey(dependencies);

    useEffect(() => {
        const ids = options.map(item => subscribe(topic, item, message => listenerRef.current(message)));
        return () => ids.forEach(unsubscribe);
    }, [topic, optionsKey, dependenciesKey, subscribe, unsubscribe]);
}

export function useLiveUpdateArrayState<T extends { _id: string }>(topic: LiveUpdateTopic, initial: T[], options?: Record<string, unknown>, dependencies: DependencyList = []) {
    const [state, setState] = useState(initial);
    const resetKey = dependencyKey(dependencies);

    useEffect(() => setState(initial), [resetKey]);

    useLiveUpdate(topic, options, message => {
        setState(current => {
            const id = typeof message.data === 'string' ? message.data : message.data?._id;
            if (!id) return current;
            if (message.op === 'delete') return current.filter(item => item._id !== id);

            const index = current.findIndex(item => item._id === id);
            if (message.op === 'create') {
                if (index === -1) return [...current, message.data as T];
                return current.map((item, currentIndex) => currentIndex === index ? { ...item, ...message.data } : item);
            }
            if (index === -1) return current;
            return current.map((item, currentIndex) => currentIndex === index ? { ...item, ...message.data } : item);
        });
    }, dependencies);

    return [state, setState] as const;
}

export function useLiveUpdateObjectState<T extends { _id: string }>(topic: LiveUpdateTopic, initial: T, options?: Record<string, unknown>, dependencies: DependencyList = []) {
    const [state, setState] = useState(initial);
    const resetKey = dependencyKey(dependencies);

    useEffect(() => setState(initial), [resetKey]);

    useLiveUpdate(topic, options ?? { _id: initial._id }, message => {
        const id = typeof message.data === 'string' ? message.data : message.data?._id;
        if (id !== state._id) return;
        if (message.op === 'delete') return;
        setState(current => ({ ...current, ...message.data }));
    }, dependencies);

    return [state, setState] as const;
}

export function useLiveUpdateStatus() {
    return useContext(Context);
}
